import tw from 'twin.macro'
import React, { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Combobox } from '@headlessui/react'
import { CgChevronDown } from 'react-icons/cg'
import * as Portal from '@radix-ui/react-portal'

import { SuggestionContainer, SuggestionItem } from 'common/components/Suggestions/Suggestions'
import { inputBase } from 'common/components/Input/Input'
import { usePopper } from 'common/hooks/usePopper'
import {
  getAllTimeZones,
  getSelectableTimeZoneName,
  getTimeZoneSecondaryLine,
  getTimeZoneTitle,
  timeZoneMatchesSearch,
} from '../timeZoneMetadata'

// atTime is the call's start time. The offsets shown next to each zone are read at
// that moment rather than at today's date, so a call booked in July for December
// shows the winter offset the call will really run at.
export default function TimeZoneInput({ inputRef, value, onChange, clearErrors, error, atTime }) {
  const comboboxBtnRef = useRef()
  const [query, setQuery] = useState('')
  const timeZones = useMemo(() => getAllTimeZones(atTime), [atTime])

  const filteredTimeZones = useMemo(() => {
    if (!query.trim()) return timeZones
    return timeZones.filter((zone) => timeZoneMatchesSearch(zone, query, atTime))
  }, [query, timeZones, atTime])

  const handleInputKeyDown = (e) => {
    const acceptedCodes = ['Enter', 'Tab', 'ArrowUp', 'ArrowDown', 'Backspace', 'Delete']
    if (e.code === 'Space') {
      e.stopPropagation()
      return
    }
    if (e.code === 'Enter' && !e.shiftKey) {
      comboboxBtnRef.current.click()
    } else if (!acceptedCodes.includes(e.code) && e.key.length !== 1) {
      e.preventDefault()
    }
  }

  const popperOptions = useMemo(
    () => ({
      placement: 'bottom-start',
      strategy: 'fixed',
      modifiers: [
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'flip', options: { fallbackPlacements: ['top-start'], padding: 8 } },
        { name: 'preventOverflow', options: { padding: 8 } },
      ],
    }),
    []
  )
  let [popperTrigger, popperContainer] = usePopper(popperOptions)

  // The options list gets its width from this measurement. Measured in a layout
  // effect like the other suggestion inputs: at first render the input isn't
  // mounted yet, and a render-time measurement stays undefined until something
  // else re-renders this component — so opening the dropdown right after
  // choosing the group call button type would show an unsized menu.
  const [inputWidth, setInputWidth] = useState(undefined)
  useLayoutEffect(() => {
    setInputWidth(inputRef.current?.getBoundingClientRect()?.width)
  }, [inputRef])

  return (
    <div className='mb-4'>
      <label className='capitalize inline-flex cursor-pointer font-semibold text-tBlack' tw='mb-1'>
        Timezone
      </label>
      <Combobox
        value={getSelectableTimeZoneName(value)}
        onChange={(newValue) => {
          clearErrors('callTimeZone')
          setQuery('')
          onChange(newValue)
        }}
      >
        <div ref={popperTrigger} className='w-full'>
          <Combobox.Button ref={comboboxBtnRef} as='div' className='relative w-full'>
            <Combobox.Input
              ref={(e) => {
                inputRef.current = e
              }}
              placeholder='Search timezones'
              aria-label='Timezone'
              autoComplete='off'
              displayValue={(timeZone) => getTimeZoneTitle(timeZone)}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleInputKeyDown}
              className='text-ellipsis bg-inherit'
              css={[inputBase, error && tw`ring-tRed focus:ring-tRed`]}
            />
            <CgChevronDown className='absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500 w-4 h-4' />
          </Combobox.Button>
        </div>
        {filteredTimeZones.length > 0 && (
          <Portal.Root>
            <Combobox.Options className='z-[999]' ref={popperContainer} modal={false}>
              <SuggestionContainer
                containerClasses='!static !top-0'
                containerStyle={{ width: inputWidth }}
                scrollAreaClasses='!max-h-72'
              >
                {filteredTimeZones.map((zone) => (
                  <Combobox.Option as={Fragment} key={zone.name} value={zone.name}>
                    {({ active }) => (
                      <SuggestionItem active={active}>
                        <div className='flex flex-col'>
                          <span>{getTimeZoneTitle(zone)}</span>
                          <span className='text-xs text-gray-500'>{getTimeZoneSecondaryLine(zone, atTime)}</span>
                        </div>
                      </SuggestionItem>
                    )}
                  </Combobox.Option>
                ))}
              </SuggestionContainer>
            </Combobox.Options>
          </Portal.Root>
        )}
      </Combobox>
      {error && <p className='flex text-xs mt-1 text-tRed'>{error.message}</p>}
    </div>
  )
}
